import { Box, Button, styled, Tooltip, Typography } from '@mui/material';
import { Recommendation, SubBlockType } from 'common/@types/recommendations';
import useAuth from 'common/hooks/useAuth';
import useModal from 'common/hooks/useModal';
import { UNBLOCK_OVERRIDE_STATUS } from 'dash/src/pages/learn/recommendations';
import { useEffect, useMemo, useState } from 'react';
import LessonTestAccordian from './LessonTestAccordion';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

type Props = {
  rows: Recommendation[];
  renderUrlContent: (
    row: Recommendation,
    field: 'suggested_content' | 'url'
  ) => React.JSX.Element | undefined;
  hideGrade?: boolean;
  campusId: number;
  irvingtonCampusId: number;
  irvingtonUnblockCount: number;
};

const CustomStuckButton = styled(Button)(({ theme }) => ({
  marginRight: '20px',
  fontSize: '14px',
  '&:hover': {
    backgroundColor: '#dceff5bd',
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: '13px',
  },
}));

const LessonTypeTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '14px',
  width: '100%',
  padding: '10px',
  height: '60px',
  alignItems: 'center',
  justifyContent: 'start',
  fontSize: '18px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[200],
  borderRadius: '8px 8px 0px 0px',
}));
export const UNBLOCKABLE_SOURCE_ID = 6;
export const UNBLOCK_STATUS = 'unblocked';

const ADMIN_EMAIL_WHITELIST = new Set([
  'ccolangelo@readingschools.org',
  'sarah.williamson@lipscomb.edu',
  'kristi.reynolds@lipscomb.edu',
  'rachel.opersteny@onehopeschools.org',
  'michael.peters@onehopeschools.org',
  'meagan.peters@onehopeschools.org',
  'tech@onehopeacademy.org',
  'kevin.clinard@onehopeschools.org',
  'amwoodward@readingschools.org',
  'lbruewitz@readingschools.org',
  'ccolangelo@readingschools.org',
  'ccrain@readingschools.org',
  'droberts@readingschools.org',
  'mkron@readingschools.org',
  'lbruewitz@readingschools.org',
  'lkloesz@readingschools.org',
  'mcook@readingschools.org',
  'rreynolds@readingschools.org',
  'ttumbusch@readingschools.org',
  'kbmccaslin43@gmail.com',
  'ignacio@lfgacademy.org',
  'rachel.opersteny@onehopeschools.org',
  'alysia.mcclung@onehopeschools.org',
  'kevin.clinard@onehopeschools.org',
  'parker.koesel@onehopeschools.org',
  'amanda.bruce@ortrail.k12.or.us',
  'clinton.davis@ortrail.k12.or.us',
  'daniel.winans@ortrail.k12.or.us',
  'david.beitler@ortrail.k12.or.us',
  'edgar.montellano@ortrail.k12.or.us',
  'huston.emmons@ortrail.k12.or.us',
  'jenifer.gross@ortrail.k12.or.us',
  'jennifer.johnson@ortrail.k12.or.us',
  'jeremy.wedell@ortrail.k12.or.us',
  'jessica.minch@ortrail.k12.or.us',
  'kalee.robertson@ortrail.k12.or.us',
  'kim.gallucci@ortrail.k12.or.us',
  'lan.green@ortrail.k12.or.us',
  'lindsay.jacksha@ortrail.k12.or.us',
  'lindsay.polk@ortrail.k12.or.us',
  'trevor.jones@ortrail.k12.or.us',
  'molly.marple@ortrail.k12.or.us',
  'tiffany.pfenning@ortrail.k12.or.us',
  'maria.perez@ortrail.k12.or.us',
  'marisa.hazelton@ortrail.k12.or.us',
  'maya.cooney@ortrail.k12.or.us',
  'ryan.cauch@ortrail.k12.or.us',
  'megan.montellano@ortrail.k12.or.us',
  'natalia.hamalainen@ortrail.k12.or.us',
  'nicole.sellin@ortrail.k12.or.us',
  'nicole.kellim@ortrail.k12.or.us',
  'ryan.shanks@ortrail.k12.or.us',
  'samantha.scherer@ortrail.k12.or.us',
  'sara.king@ortrail.k12.or.us',
  'sarah.mcintyre@ortrail.k12.or.us',
  'shara.nagel@ortrail.k12.or.us',
  'sharon.nobel@ortrail.k12.or.us',
  'sophia.kahl@ortrail.k12.or.us',
  'taylor.fortin@ortrail.k12.or.us',
  'taylor.sestric@ortrail.k12.or.us',
  'wendy.reese@ortrail.k12.or.us',
  'zachariah.duell@ortrail.k12.or.us',
  'krackley11@gmail.com',
  'nataliepineda@hotmail.com',
  'kbmccaslin43@gmail.com',
  'wendy.reese@ortrail.k12.or.us',
  'tracy.wells@ortrail.k12.or.us',
  'alyssa.strongsmith@ortrail.k12.or.us',
  'jan.sharman@ortrail.k12.or.us',
  'kelly.perry@ortrail.k12.or.us',
  'porterk@acs.k12.sc.us',
  'tavaresn@acs.k12.sc.us',
  'breanna.brant@acs.k12.sc.us',
  'kandulas@acs.k12.sc.us',
  'budhus@acs.k12.sc.us',
  'jacksonsa@acs.k12.sc.us',
  'seasea@acs.k12.sc.us',
  'haughtona@acs.k12.sc.us',
  'folkb@acs.k12.sc.us',
  'whitley-rogerse@acs.k12.sc.us',
  'gary.jameson@acs.k12.sc.us',
  'isadore.ray@acs.k12.sc.us',
  'jessica.grant@acs.k12.sc.us',
  'morgans@acs.k12.sc.us',
  'coxs@acs.k12.sc.us',
  'dabanganat@acs.k12.sc.us',
  'reidz@acs.k12.sc.us',
  'marcus.boston@acs.k12.sc.us',
  'busbyp@acs.k12.sc.us',
  'campolongop@acs.k12.sc.us',
  'bailey.thompson@jcsd.net',
  'carol.thompson@jcsd.net',
  'doris.jones@jcsd.net',
  'elieen.shaprio@jcsd.net',
  'fridah.kawasha@jcsd.net',
  'john_bradley.apostol@jcsd.net',
  'jennings.lakieshie@jcsd.net',
  'lemuel.mosquito@jcsd.net',
  'mahogany.salisbury@jcsd.net',
  'mary.robertson@jcsd.net',
  'milessa.minott@jcsd.net',
  'rhonda.gill@jcsd.net',
  'rodel.bantad@jcsd.net',
  'roger.reid@jcsd.net',
  'terrie.bryans@jcsd.net',
  'toni.case@jcsd.net',
  'vestina.kenon@jcsd.net',
  'ashley.williams@jcsd.net',
  'avlen.edwards@jcsd.net',
  'jannie.barnes@jcsd.net',
  'muntasir.hamid@jcsd.net',
  'peterkim.pusey@jcsd.net',
  'sandra.bickerstaff@jcsd.net',
  'shantireya.rivers@jcsd.net',
  'tashanique.douglas@jcsd.net',
  'timothy.saxton@jcsd.net',
  'blake.raley@jcsd.net',
  'tamekia.ford@jcsd.net',
  'shaun.shelton+scjhdms@jcsd.net',
  'dawn.thayer+scjhdms@jcsd.net',
  'renata.green@jcsd.net',
  'paula.murphy@jcsd.net',
  'shaun.shelton+scjrde@jcsd.net',
  'dawn.thayer+scjrde@jcsd.net',
  'tonya.robinson@jcsd.net',
  'jasmine.turkvan@jcsd.net',
  'kimberly.ball+scjrde@studient.com',
  'tony.disario+scjrde@studient.com',
  'adrienne.maybank@jcsd.net',
  'alexis.calloway@jcsd.net',
  'allison.atkins+scjrds@studient.com',
  'eunice.kimani@jcsd.net',
  'janice.arriesgado@jcsd.net',
  'lavanya.malkar@jcsd.net',
  'mary_grace.delfinado@jcsd.net',
  'rivera@themetroschool.org',
  'peaks@themetroschool.org',
  'sander.1@themetroschool.org',
  'skinner@themetroschool.org',
  'hanlon@themetroschool.org',
  'singhal@themetroschool.org',
  'armstrong@themetroschool.org',
  'venkatasamyr@acs.k12.sc.us',
  'cavec@acs.k12.sc.us',
  'priesterr@acs.k12.sc.us',
]);

export const isRecCompleted = (recommendation: Recommendation) => {
  return (
    recommendation.completed_on ||
    recommendation.status === 'completed' ||
    (recommendation.smart_score === 100 &&
      !['Knowledge Gap', 'Knowledge Gap Override'].includes(recommendation.recommendation_source))
  );
};

// Disable button => [GHI-12166, GHI-12449]
const studentIdsForStuckBtnBlock = ['0692', '0368', '0759', '0239'];

const getBlockingStatus = (
  index: number,
  currentRec: Recommendation,
  inCompleteRecIndex: number,
  campusId: number,
  irvingtonCampusId: number,
  irvingtonUnblockCount: number,
  rows: Recommendation[]
) => {
  // Determine if the current recommendation is completed
  const isCompleted = isRecCompleted(currentRec);

  // Determine if the current recommendation is explicitly unblocked
  const isExplicitlyUnblocked =
    currentRec.status === UNBLOCK_STATUS ||
    currentRec.status === UNBLOCK_OVERRIDE_STATUS ||
    currentRec.source_id !== UNBLOCKABLE_SOURCE_ID;

  if (campusId === irvingtonCampusId && currentRec.type === 'learn') {
    // For Irvington campus, count completed and unblocked recommendations before the current index
    const completedBeforeIndex = rows.slice(0, index).filter((rec) => isRecCompleted(rec)).length;

    const unblockBeforeIndex = rows
      .slice(0, index)
      .filter(
        (rec) =>
          !isRecCompleted(rec) &&
          (rec.status === UNBLOCK_STATUS ||
            rec.status === UNBLOCK_OVERRIDE_STATUS ||
            rec.source_id !== UNBLOCKABLE_SOURCE_ID)
      ).length;

    // Allow irvingtonUnblockCount number of recommendations plus any already unblocked ones
    const unblockedLimit = irvingtonUnblockCount + completedBeforeIndex + unblockBeforeIndex;
    return index >= unblockedLimit && !isCompleted && !isExplicitlyUnblocked;
  } else {
    // For non-Irvington campus, block if not the first incomplete recommendation
    return index > inCompleteRecIndex && !isCompleted && !isExplicitlyUnblocked;
  }
};

const AccordionListingContainer = ({
  rows,
  renderUrlContent,
  hideGrade,
  campusId,
  irvingtonCampusId,
  irvingtonUnblockCount,
}: Props) => {
  const { user } = useAuth();
  const studentId = rows[0]?.student_id;
  const showStuckButton = !studentIdsForStuckBtnBlock.includes(studentId);
  const [expandedRecommendationId, setExpandedRecommendation] = useState<number>(0);
  const [amIStuck, setAmIStuck] = useState<boolean>(false);
  const { toggleLessonUnblockModal } = useModal();
  const role = user?.alphaAuthAttributes?.role || 'Student';
  const userEmail = (user?.attributes?.email || user?.email || '').toLowerCase();
  const isAdmin = ['Admin', 'Guide'].includes(role) || ADMIN_EMAIL_WHITELIST.has(userEmail);
  const {
    filteredRows: testRecs,
    incompleteRecommendation: inCompleteTest,
    incompleteRecIndex: inCompleteTestIndex,
  } = useRecommendations(
    rows,
    SubBlockType.TEST,
    campusId,
    irvingtonCampusId,
    irvingtonUnblockCount
  );
  const {
    filteredRows: learnRecs,
    incompleteRecommendation: inCompleteLearn,
    incompleteRecIndex: inCompleteLearnIndex,
    hasSuportingIncomplete,
    inCompleteNextRecommendation,
  } = useRecommendations(
    rows,
    SubBlockType.LEARN,
    campusId,
    irvingtonCampusId,
    irvingtonUnblockCount
  );

  console.log('Incomplete Next Recommendation:', inCompleteNextRecommendation);
  const handleChange = (recommendationId: number) => {
    setExpandedRecommendation(expandedRecommendationId === recommendationId ? 0 : recommendationId);
  };

  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => console.log('Calendly script loaded');
    script.onerror = () => console.error('Failed to load Calendly script');
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openCalendlyModal = () => {
    if (window.Calendly) {
      // Custom URL for Unbound
      const url =
        campusId === 40
          ? 'https://calendly.com/d/cws4-gbj-nx9'
          : 'https://calendly.com/d/cnvt-rv7-qs8';
      window.Calendly.initPopupWidget({
        url,
      });
      setAmIStuck((prevState) => !prevState);
    } else {
      console.error('Calendly object not found');
    }
  };

  const unblockLessonOption = useMemo(
    () => (rec?: Recommendation, hasSuportingIncomplete?: boolean) => {
      if (!rec) return null;
      return (
        <Box display="flex" flexDirection="row">
          <Tooltip title="Click here if you are stuck and need assistance to progress to the next lesson.">
            <CustomStuckButton
              variant="outlined"
              color="primary"
              onClick={() => setAmIStuck((prevState) => !prevState)}
            >
              I'm stuck
            </CustomStuckButton>
          </Tooltip>
          {amIStuck && isAdmin && (
            <Tooltip title="Click here if you are stuck and need to unblock the next lesson.">
              <CustomStuckButton
                variant="outlined"
                color="info"
                onClick={() => {
                  toggleLessonUnblockModal({
                    recommendation: rec,
                    type: 'unblock',
                    unblockableRecs: inCompleteNextRecommendation,
                  });
                  setAmIStuck((prevState) => !prevState);
                }}
              >
                Unblock next lesson?
              </CustomStuckButton>
            </Tooltip>
          )}
          {amIStuck && (isAdmin || (!isAdmin && showStuckButton)) && (
            <Tooltip title="Click here if you are stuck and need to add a support lesson.">
              <CustomStuckButton
                disabled={hasSuportingIncomplete}
                variant="outlined"
                color="primary"
                onClick={() => {
                  toggleLessonUnblockModal({
                    recommendation: rec,
                    type: 'support',
                    unblockableRecs: inCompleteNextRecommendation,
                  });
                  setAmIStuck((prevState) => !prevState);
                }}
              >
                Add supporting Lesson
              </CustomStuckButton>
            </Tooltip>
          )}
          {amIStuck && (
            <Tooltip title="Click here if you want to book a coaching call.">
              <CustomStuckButton
                variant="outlined"
                color="primary"
                onClick={() => {
                  openCalendlyModal();
                }}
              >
                Book a coaching call
              </CustomStuckButton>
            </Tooltip>
          )}
        </Box>
      );
    },
    [toggleLessonUnblockModal, isAdmin, amIStuck]
  );

  const RenderListing = useMemo(() => {
    // Declare variables for indexing
    let quizIndex = 0;
    let lessonIndex = 0;
    return (
      recs: Recommendation[],
      type: SubBlockType,
      expandedRecId: number,
      inCompleteRec: Recommendation,
      inCompleteRecIndex: number,
      hasSuportingIncomplete: boolean
    ) => (
      <Box>
        {recs.length > 0 && (
          <LessonTypeTitle>
            <span># {type === SubBlockType.TEST ? 'Tests' : 'Lessons'}</span>
            {unblockLessonOption(inCompleteRec, hasSuportingIncomplete)}
          </LessonTypeTitle>
        )}
        {recs.map((recommendation: Recommendation, i) => {
          const isQuiz = type === 'learn' && recommendation.name?.toLowerCase().includes('quiz');
          const currentIndex = isQuiz ? quizIndex++ : lessonIndex++;
          return (
            <LessonTestAccordian
              index={currentIndex}
              key={recommendation.uuid}
              recommendation={recommendation}
              expanded={expandedRecId}
              handleChange={handleChange}
              type={type}
              renderUrlContent={renderUrlContent}
              blocked={getBlockingStatus(
                i,
                recommendation,
                inCompleteRecIndex,
                campusId,
                irvingtonCampusId,
                irvingtonUnblockCount,
                recs
              )}
              hideGrade={hideGrade}
            />
          );
        })}
      </Box>
    );
  }, [unblockLessonOption, renderUrlContent, handleChange]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        gap: '10px',
      }}
    >
      {RenderListing(
        testRecs,
        SubBlockType.TEST,
        expandedRecommendationId,
        inCompleteTest,
        inCompleteTestIndex,
        false
      )}
      {RenderListing(
        learnRecs,
        SubBlockType.LEARN,
        expandedRecommendationId,
        inCompleteLearn,
        inCompleteLearnIndex,
        hasSuportingIncomplete
      )}
    </div>
  );
};

const useRecommendations = (
  rows: Recommendation[],
  type: SubBlockType,
  campusId: number,
  irvingtonCampusId: number,
  irvingtonUnblockCount: number
) => {
  return useMemo(() => {
    const filteredRows = rows.filter((rec) => rec.type === type);
    const hasSuportingIncomplete = filteredRows.some(
      (rec) => rec.recommendation_source === 'Supporting Skills' && !isRecCompleted(rec)
    );

    // Find the index of the first incomplete recommendation
    const incompleteRecIndex = filteredRows.findIndex(
      (rec) =>
        !isRecCompleted(rec) &&
        rec.status !== UNBLOCK_STATUS &&
        rec.source_id === UNBLOCKABLE_SOURCE_ID
    );

    // Get all unblocked recommendations
    const inCompleteNextRecommendation = filteredRows.filter((rec, index) => {
      const validRecommendationSource = [
        'Skill Plan',
        'Knowledge Gap',
        'Academics Override',
      ].includes(rec.recommendation_source);
      return (
        !isRecCompleted(rec) &&
        validRecommendationSource &&
        !getBlockingStatus(
          index,
          rec,
          incompleteRecIndex,
          campusId,
          irvingtonCampusId,
          irvingtonUnblockCount,
          filteredRows
        )
      );
    });

    return {
      filteredRows,
      incompleteRecommendation: filteredRows[incompleteRecIndex],
      incompleteRecIndex,
      hasSuportingIncomplete,
      inCompleteNextRecommendation,
    };
  }, [rows, type]);
};

export default AccordionListingContainer;
